import React, { useEffect, useMemo, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { Button, Card, Col, Form, Modal, Nav, Row, Spinner, Table, Tab } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import moment from "moment";
import CurrentDateComponents from "../../components/CurrentDateComponents";
import { toast } from "react-toastify";
import { GET_REPORT_CUSTOMER_ORDERS, reportJackSportFabricFinanceDetail } from "../../apollo/dashboard/query";
import { GET_ORDER_GROUPS_V2 } from "../../apollo/ordergroup/query";
import { USER_KEY } from "../../const";
import AnimationLoading from "../../components/Animationloading";
import EmptyComponent from "../../components/EmptyComponent";

export default function ReportCustomerByPhone() {
  const { t } = useTranslation();
  const shopId = JSON.parse(localStorage.getItem(USER_KEY))?.data?.shop?.id;
  const [activeTab, setActiveTab] = useState("phone");

  // Tab 1: Phone Report States
  const [searchPhone, setSearchPhone] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedPhone, setSelectedPhone] = useState("");
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Tab 2: Finance Report States (Defaults to current month)
  const [financeFrom, setFinanceFrom] = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [financeTo, setFinanceTo] = useState(moment().endOf("month").format("YYYY-MM-DD"));
  const [openDateModal, setOpenDateModal] = useState(false);
  const [activeButton, setActiveButton] = useState("thisMonth");

  const [loadOrders, { data, loading }] = useLazyQuery(GET_REPORT_CUSTOMER_ORDERS, { fetchPolicy: "network-only" });
  const [loadOrderGroups, { data: orderGroupsData, loading: loadingOrderGroups }] = useLazyQuery(GET_ORDER_GROUPS_V2, { fetchPolicy: "network-only" });
  const [loadFinance, { data: financeData, loading: loadingFinance }] = useLazyQuery(reportJackSportFabricFinanceDetail, { fetchPolicy: "network-only" });

  const datas = data?.reportCustomerOrders?.data || [];
  const orderGroups = (orderGroupsData?.orderGroupsV2?.data || []).filter((o) => o?.isPaymented === true);
  const financeReport = financeData?.reportJacksportFabricFinanceDetailed || {};

  const grouped = useMemo(() => {
    let arr = datas
      .map((o) => ({
        customerId: o?.customerId,
        customerName: o?.customerName ?? "-",
        customerLastName: o?.customerLastName ?? "",
        phone: (o?.phone || "").trim() || "-",
        totalOrders: Number(o?.totalOrders || 0),
      }))
      .filter((o) => (searchPhone ? o.phone.includes(searchPhone) : true));

    return arr.sort((a, b) => b.totalOrders - a.totalOrders);
  }, [datas, searchPhone]);

  useEffect(() => {
    if (activeTab === "phone") {
      const where = {
        shop: shopId,
        isPaymented: true,
        ...(dateFrom ? { createdAt_gte: moment(dateFrom).startOf("day").toISOString() } : {}),
        ...(dateTo ? { createdAt_lt: moment(dateTo).endOf("day").toISOString() } : {}),
        ...(searchPhone ? { phone: searchPhone } : {}),
      };
      loadOrders({ variables: { where, limit: 1000, skip: 0 } });
    } else if (activeTab === "finance") {
      const where = {
        shop: shopId,
        ...(financeFrom ? { createdAt_gte: moment(financeFrom).startOf("day").toISOString() } : {}),
        ...(financeTo ? { createdAt_lt: moment(financeTo).endOf("day").toISOString() } : {}),
      };
      loadFinance({ variables: { where } });
    }
  }, [shopId, dateFrom, dateTo, searchPhone, loadOrders, activeTab, financeFrom, financeTo, loadFinance]);

  const fmt = (n) => new Intl.NumberFormat().format(Number(n || 0));
  
  const openDetail = async (phone) => {
    setSelectedPhone(phone);
    setShowDetailModal(true);
    const where = {
      shop: shopId,
      phone: phone,
      isDeleted: false,
      isPaymented: true,
      ...(dateFrom ? { createdAt_gte: moment(dateFrom).startOf("day").toISOString() } : {}),
      ...(dateTo ? { createdAt_lt: moment(dateTo).endOf("day").toISOString() } : {}),
    };
    await loadOrderGroups({ variables: { where, orderBy: "createdAt_DESC", limit: 200, skip: 0 } });
  };

  const mapProductionStatus = (status) => {
    if (!status) return "-";
    const mapping = {
      "PRODUCTION_ORDER": t("ps_production_order") || "ອອກບິນຜະລິດ",
      "PATTERN": t("ps_pattern") || "ກຳລັງວາງແບບ",
      "PRINTING": t("ps_printing") || "ກຳລັງພິມ",
      "HEAT_TRANSFER": t("ps_heat_transfer") || "ກຳລັງລີດລົງຜ້າ",
      "CUTTING": t("ps_cutting") || "ກຳລັງຕັດ",
      "SEWING": t("ps_sewing") || "ກຳລັງຫຍິບ",
      "QC": t("ps_qc") || "ກຳລັງກວດກາຄຸນະພາບ",
      "READY_TO_COMPLETE": t("ps_ready_to_complete") || "ລໍຖ້າຢືນຢັນສຳເລັດ",
      "COMPLETED": t("ps_completed") || "ສຳເລັດ",
      "PENDING": t("po_pending") || "ລໍຖ້າກວດສອບ",
      "CANCELLED": t("po_cancelled") || "ປະຕິເສດ",
    };
    return mapping[status] || status;
  };

  const renderPhoneReport = () => (
    <>
      <Card className="p-3 mb-3 border-0 shadow-sm" style={{ borderRadius: "12px" }}>
        <Row className="g-2">
          <Col md={3}>
            <Form.Label>{t("phone") || "ເບີໂທ"}</Form.Label>
            <Form.Control
              placeholder={t("phone") || "ເບີໂທ"}
              value={searchPhone}
              onChange={(e) => setSearchPhone(e.target.value)}
            />
          </Col>
          <Col md={3}>
            <Form.Label>{t("from") || "ຈາກວັນທີ"}</Form.Label>
            <Form.Control
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </Col>
          <Col md={3}>
            <Form.Label>{t("to") || "ເຖິງວັນທີ"}</Form.Label>
            <Form.Control
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </Col>
        </Row>
      </Card>
      {loading ? (
        <AnimationLoading />
      ) : grouped.length === 0 ? (
        <EmptyComponent />
      ) : (
        <Card className="p-3 border-0 shadow-sm" style={{ borderRadius: "12px" }}>
          <Table responsive hover size="sm">
            <thead>
              <tr>
                <th>#</th>
                <th>{t("customer_name") || "ຊື່ລູກຄ້າ"}</th>
                <th>{t("phone") || "ເບີໂທ"}</th>
                <th>{t("orders_count") || "ຈຳນວນຄັ້ງທີ່ຊື້"}</th>
                <th>{t("detail") || "ລາຍລະອຽດ"}</th>
              </tr>
            </thead>
            <tbody>
              {grouped.map((r, idx) => (
                <tr key={(r.customerId || r.phone) + idx}>
                  <td>{idx + 1}</td>
                  <td>{`${r.customerName} ${r.customerLastName || ""}`.trim()}</td>
                  <td>{r.phone}</td>
                  <td>{fmt(r.totalOrders)}</td>
                  <td>
                    <Button size="sm" variant="outline-primary" onClick={() => openDetail(r.phone)}>
                      {t("detail") || "ລາຍລະອຽດ"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
    </>
  );

  const renderFinanceReport = () => (
    <>
      <Card className="p-3 mb-4 border-0 shadow-sm" style={{ borderRadius: "12px" }}>
        <Row className="g-3 align-items-center mb-3">
          <Col md="auto">
            <Button 
              variant="light" 
              className="border d-flex align-items-center gap-2 px-3 py-2 shadow-sm" 
              onClick={() => setOpenDateModal(true)}
              style={{ borderRadius: "10px", background: "#fff", fontWeight: "600" }}
            >
              <span style={{ fontSize: "1.2rem" }}>📅</span>
              <div className="text-start">
                <small className="text-muted d-block" style={{ fontSize: "0.75rem", lineHeight: "1" }}>{t("select_date") || "ເລືອກວັນທີ"}</small>
                <span>{moment(financeFrom).format("DD/MM/YYYY")} - {moment(financeTo).format("DD/MM/YYYY")}</span>
              </div>
            </Button>
          </Col>
          {/* <Col md={2}>
            <Button 
              variant="primary" 
              className="w-100 py-2 shadow-sm" 
              style={{ borderRadius: "10px", fontWeight: "bold" }}
              onClick={() => loadFinance({ 
                variables: { 
                  where: { 
                    shop: shopId, 
                    createdAt_gte: moment(financeFrom).startOf("day").toISOString(), 
                    createdAt_lt: moment(financeTo).endOf("day").toISOString() 
                  } 
                } 
              })}
            >
              {t("search_data") || "ຄົ້ນຫາ"}
            </Button>
          </Col> */}
        </Row>
      </Card>

      {loadingFinance ? (
        <AnimationLoading />
      ) : (
        <>
          <Row className="mb-4 g-3">
            <Col md={3}>
              <Card className="p-3 border-0 shadow-sm h-100" style={{ borderRadius: "15px", background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white" }}>
                <small className="opacity-75">{t("total_income_sold") || "ລາຍໄດ້ທັງໝົດ (ສິນຄ້າຂາຍ)"}</small>
                <h3 className="mb-0 mt-2" style={{ fontWeight: "bold" }}>₭ {fmt(financeReport.totalSoldIncome)}</h3>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="p-3 border-0 shadow-sm h-100" style={{ borderRadius: "15px", background: "linear-gradient(135deg, #ff6b6b 0%, #ee5253 100%)", color: "white" }}>
                <small className="opacity-75">{t("total_expense_bought") || "ລາຍຈ່າຍທັງໝົດ (ສັ່ງຊື້ຜ້າ)"}</small>
                <h3 className="mb-0 mt-2" style={{ fontWeight: "bold" }}>₭ {fmt(financeReport.totalBoughtExpense)}</h3>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="p-3 border-0 shadow-sm h-100" style={{ borderRadius: "15px", background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", color: "white" }}>
                <small className="opacity-75">{t("gross_profit") || "ກຳໄລຂັ້ນຕົ້ນ"}</small>
                <h3 className="mb-0 mt-2" style={{ fontWeight: "bold" }}>₭ {fmt(financeReport.totalGrossProfit)}</h3>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="p-3 border-0 shadow-sm h-100" style={{ borderRadius: "15px", background: "rgba(255, 255, 255, 0.9)" }}>
                <small className="text-muted">{t("total_sold_free") || "ຂາຍອອກທັງໝົດ / ແຖມ"}</small>
                <h3 className="mb-0 mt-2 text-dark" style={{ fontWeight: "bold" }}>{fmt(financeReport.totalSoldAmount)} / {fmt(financeReport.totalSoldFreeAmount)}</h3>
              </Card>
            </Col>
          </Row>

          <Card className="p-3 border-0 shadow-sm" style={{ borderRadius: "15px" }}>
            <h6 className="mb-3" style={{ fontWeight: "bold" }}>{t("detail_by_fabric_type") || "ລາຍລະອຽດແຍກຕາມປະເພດຜ້າ"}</h6>
            <Table responsive hover className="mb-0">
              <thead className="">
                <tr>
                  <th className="border-0">{t("fabric_name") || "ຊື່ຜ້າ"}</th>
                  <th className="border-0 text-center">{t("purchase_order") || "ສັ່ງຊື້"}</th>
                  <th className="border-0 text-center">{t("sold_free") || "ຂາຍອອກ (ແຖມ)"}</th>
                  <th className="border-0 text-end">{t("income") || "ລາຍໄດ້"}</th>
                  <th className="border-0 text-end">{t("expense") || "ລາຍຈ່າຍ"}</th>
                  <th className="border-0 text-end">{t("profit") || "ກຳໄລ"}</th>
                  <th className="border-0 text-center">{t("remaining_stock") || "ຍອດເຫຼືອສາງ"}</th>
                </tr>
              </thead>
              <tbody>
                {financeReport.details?.map((d, i) => (
                  <tr key={i}>
                    <td><span style={{ fontWeight: "500" }}>{d.fabricName}</span></td>
                    <td className="text-center">{fmt(d.boughtAmount)} {t("roll_unit") || "ກໍ້"}</td>
                    <td className="text-center">{fmt(d.soldAmount)} ({fmt(d.soldFreeAmount)})</td>
                    <td className="text-end text-primary">₭ {fmt(d.soldIncome)}</td>
                    <td className="text-end text-danger">₭ {fmt(d.boughtExpense)}</td>
                    <td className="text-end" style={{ color: d.grossProfit >= 0 ? "#10b981" : "#ef4444", fontWeight: "bold" }}>₭ {fmt(d.grossProfit)}</td>
                    <td className="text-center">
                      <span className={`badge ${d.currentTotalQty < 100 ? "bg-warning" : "bg-info"}`} style={{ padding: "8px 12px", borderRadius: "8px" }}>
                        {fmt(d.currentTotalQty)} {t("shirt_unit") || "ເສື້ອ"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </>
      )}
    </>
  );

  return (
    <div className="p-4" style={{ backgroundColor: "#F4F7FE", minHeight: "100vh" }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 style={{ fontWeight: "800", color: "#2B3674", margin: 0 }}>{t("report_for_jacksport")}</h4>
      </div>

      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
        <Nav variant="pills" className="mb-4 p-1 bg-white shadow-sm" style={{ borderRadius: "12px", width: "fit-content" }}>
          <Nav.Item>
            <Nav.Link eventKey="phone" className="px-4 py-2" style={{ borderRadius: "10px", fontWeight: "bold" }}>
              {t("report_customer") || "ລາຍງານລູກຄ້າ"}
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="finance" className="px-4 py-2" style={{ borderRadius: "10px", fontWeight: "bold" }}>
              {t("overview_report") || "ລາຍງານພາບລວມ"}
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="phone">
            {renderPhoneReport()}
          </Tab.Pane>
          <Tab.Pane eventKey="finance">
            {renderFinanceReport()}
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="xl" centered scrollable>
        <Modal.Header closeButton className="border-0 pb-2 px-4 pt-4">
          <Modal.Title style={{ fontWeight: "800", color: "#2B3674", fontSize: "1.5rem" }}>
            {t("detail") || "ລາຍລະອຽດ"}: <span className="text-primary">{selectedPhone || "-"}</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 pb-4">
          <div style={{ minHeight: "40vh" }}>
            {loadingOrderGroups ? (
              <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: 300 }}>
                <Spinner animation="border" variant="primary" style={{ width: "3rem", height: "3rem" }} />
                <p className="mt-3 text-muted">{t("loading_text") || "ກຳລັງໂຫຼດ..."}</p>
              </div>
            ) : orderGroups.length === 0 ? (
              <div style={{ minHeight: 300 }} className="d-flex align-items-center justify-content-center">
                <EmptyComponent />
              </div>
            ) : (
              <div className="card border-0 shadow-none">
                <Table responsive hover className="mb-0 overflow-hidden" style={{ borderRadius: "12px" }}>
                  <thead >
                    <tr>
                      <th className="border-0 py-3 ps-4" style={{ color: "#A3AED0", fontWeight: "600", fontSize: "0.85rem", textTransform: "uppercase" }}>#</th>
                      <th className="border-0 py-3" style={{ color: "#A3AED0", fontWeight: "600", fontSize: "0.85rem", textTransform: "uppercase" }}>{t("order_code") || "ເລກທີໃບສັ່ງຊື້"}</th>
                      <th className="border-0 py-3" style={{ color: "#A3AED0", fontWeight: "600", fontSize: "0.85rem", textTransform: "uppercase" }}>{t("day_month_year") || "ວັນທີສັ່ງຊື້"}</th>
                      <th className="border-0 py-3" style={{ color: "#A3AED0", fontWeight: "600", fontSize: "0.85rem", textTransform: "uppercase" }}>{t("status") || "ສະຖານະ"}</th>
                      <th className="border-0 py-3 text-end" style={{ color: "#A3AED0", fontWeight: "600", fontSize: "0.85rem", textTransform: "uppercase" }}>₭</th>
                      <th className="border-0 py-3 text-end" style={{ color: "#A3AED0", fontWeight: "600", fontSize: "0.85rem", textTransform: "uppercase" }}>฿</th>
                      <th className="border-0 py-3 text-end pe-4" style={{ color: "#A3AED0", fontWeight: "600", fontSize: "0.85rem", textTransform: "uppercase" }}>$</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderGroups.map((o, i) => (
                      <tr key={o?.id || i} style={{ borderBottom: "1px solid #F4F7FE" }}>
                        <td className="py-3 ps-4 align-middle" style={{ color: "#2B3674", fontWeight: "600" }}>{i + 1}</td>
                        <td className="py-3 align-middle"><span style={{ fontWeight: "700", color: "#1B2559" }}>{o?.code || "-"}</span></td>
                        <td className="py-3 align-middle" style={{ color: "#2B3674", fontSize: "0.95rem" }}>{o?.createdAt ? moment(o.createdAt).format("DD/MM/YYYY HH:mm") : "-"}</td>
                        <td className="py-3 align-middle">
                          <div 
                            className="px-3 py-1 d-inline-block" 
                            style={{ 
                              backgroundColor: (o?.productionStatus || o?.status) === "COMPLETED" ? "#E6F9F0" : "#F4F7FE", 
                              color: (o?.productionStatus || o?.status) === "COMPLETED" ? "#00B669" : "#2B3674",
                              borderRadius: "20px",
                              fontSize: "0.85rem",
                              fontWeight: "700" 
                            }}
                          >
                            {mapProductionStatus(o?.productionStatus || o?.status)}
                          </div>
                        </td>
                        <td className="py-3 text-end align-middle" style={{ fontWeight: "700", color: "#2B3674" }}>{fmt(o?.sumPrice)}</td>
                        <td className="py-3 text-end align-middle" style={{ fontWeight: "700", color: "#2B3674" }}>{fmt(o?.sumPriceBaht)}</td>
                        <td className="py-3 text-end align-middle pe-4" style={{ fontWeight: "700", color: "#2B3674" }}>{fmt(o?.sumPriceUsd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>

      <CurrentDateComponents
        openModal={openDateModal}
        onCloseModal={() => setOpenDateModal(false)}
        setActiveButton={setActiveButton}
        activeButton={activeButton}
        setCdFillter={(filter) => {
          const from = moment(filter.createdAt_gte).format("YYYY-MM-DD");
          const to = moment(filter.createdAt_lt).format("YYYY-MM-DD");
          setFinanceFrom(from);
          setFinanceTo(to);
          loadFinance({
            variables: {
              where: {
                shop: shopId,
                createdAt_gte: moment(from).startOf("day").toISOString(),
                createdAt_lt: moment(to).endOf("day").toISOString(),
              },
            },
          });
        }}
      />
    </div>
  );
}
